import { FC } from "react";
import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import ReactPlayer from "react-player";

import { CookieConsentRequired } from "components";
import { getThumbnailUrlFromMediaUrl } from "components/MediaPlayer";
import useTermlyConsent from "hooks/useTermlyConsent";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    mediaContainer: {
      width: "80%",
      marginRight: "auto",
      marginLeft: "auto",
      [theme.breakpoints.down("sm")]: { width: "100%" },
    },
    playerWrapper: {
      width: "100%",
      paddingTop: "56.25%", // 16:9 aspect ratio for responsive height
      position: "relative",
    },
    player: {
      width: "100%",
      height: "100%",
      "& iframe": {
        boxShadow: theme.shadows[3],
        borderRadius: theme.shape.borderRadius,
      },
    },
    videoBlockedPlaceholder: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundSize: "cover",
      backgroundPosition: "center",
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
    },
    overlay: {
      backdropFilter: "blur(1px)",
      background: "rgba(0,0,0,0.55)",
      color: theme.palette.background.default,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      textAlign: "center",
    },
  }),
);

interface MediaProps {
  mediaUrls?: string[];
}

const Media: FC<MediaProps> = ({ mediaUrls }) => {
  const classes = useStyles();
  const isXsDown = useMediaQuery(theme.breakpoints.down("sm"));

  const isVideoAllowed = useTermlyConsent(["performance"]);

  return (
    <Box>
      <Grid
        container
        className={classes.mediaContainer}
        spacing={isXsDown ? 2 : 10}
      >
        {mediaUrls?.map((url, index) => {
          return (
            <Grid item xs={12} key={index}>
              <Box className={classes.playerWrapper}>
                {isVideoAllowed ? (
                  <ReactPlayer
                    url={url}
                    className={classes.player}
                    controls
                    rel={0}
                    width="100%"
                    height="100%"
                    // We need to set position to absolute directly on the iframe to work inside the dialog
                    style={{ position: "absolute", top: 0, left: 0 }}
                    config={{
                      youtube: {
                        // Use privacy-enhanced mode
                        embedOptions: {
                          host: "https://www.youtube-nocookie.com",
                        },
                        playerVars: { rel: 0, modestbranding: 1 },
                      },
                    }}
                  />
                ) : (
                  <Box
                    className={classes.videoBlockedPlaceholder}
                    style={{
                      backgroundImage: `url(${getThumbnailUrlFromMediaUrl(
                        url,
                      )})`,
                      position: "absolute",
                    }}
                  >
                    <Box className={classes.overlay}>
                      <CookieConsentRequired
                        message={
                          <>
                            This video is blocked until you allow{" "}
                            <strong>Performance</strong> cookies.
                          </>
                        }
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default Media;
